import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Builds a compact page-number window with ellipses, always keeping first/last page visible.
const getPageWindow = (page, totalPages, delta) => {
    const window = [];
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    window.push(1);
    if (left > 2) window.push("left-ellipsis");
    for (let i = left; i <= right; i++) window.push(i);
    if (right < totalPages - 1) window.push("right-ellipsis");
    if (totalPages > 1) window.push(totalPages);

    return window;
};

const PageButton = ({ page, active, onClick }) => (
    <button
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        className={`px-3 py-1 rounded border transition ${
            active ? "bg-blue-600 text-white border-blue-600" : "hover:bg-slate-50"
        }`}
    >
        {page}
    </button>
);

const Ellipsis = () => (
    <span className="px-2 py-1 text-slate-400 select-none">...</span>
);

const Pagination = ({ page, totalPages, onPrevious, onNext, onPageChange }) => {
    if (!totalPages) return null;

    const goPrevious = () => {
        if (onPageChange) onPageChange(Math.max(1, page - 1));
        else onPrevious?.();
    };

    const goNext = () => {
        if (onPageChange) onPageChange(Math.min(totalPages, page + 1));
        else onNext?.();
    };

    // Backward-compatible simple "Page X of Y" pagination (Blog, Projects, admin pages).
    if (!onPageChange) {
        return (
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                <button
                    disabled={page === 1}
                    onClick={goPrevious}
                    className="w-full rounded-md border px-4 py-2 transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                    Previous
                </button>

                <span className="text-sm font-medium text-center">
                    Page {page} of {totalPages}
                </span>

                <button
                    disabled={page === totalPages}
                    onClick={goNext}
                    className="w-full rounded-md bg-blue-600 px-4 py-2 text-white transition disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                    Next
                </button>
            </div>
        );
    }

    // Windowed page-number pagination for product listing pages.
    const mobileWindow = getPageWindow(page, totalPages, 0);
    const tabletWindow = getPageWindow(page, totalPages, 1);
    const desktopWindow = getPageWindow(page, totalPages, 2);

    return (
        <nav
            aria-label="Pagination"
            className="mt-10 w-full max-w-full overflow-hidden flex justify-center"
        >
            {/* Mobile: compact controls with selectable page numbers */}
            <div className="flex sm:hidden items-center justify-center gap-1.5 max-w-full">
                <button
                    disabled={page === 1}
                    onClick={goPrevious}
                    aria-label="Previous page"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <ChevronLeft size={16} />
                </button>
                {mobileWindow.map((item, index) =>
                    typeof item === "number" ? (
                        <PageButton
                            key={item}
                            page={item}
                            active={item === page}
                            onClick={() => onPageChange(item)}
                        />
                    ) : (
                        <Ellipsis key={`${item}-${index}`} />
                    ),
                )}
                <button
                    disabled={page === totalPages}
                    onClick={goNext}
                    aria-label="Next page"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            {/* Tablet: prev/next + fewer nearby page numbers */}
            <div className="hidden sm:flex md:hidden flex-wrap items-center justify-center gap-2 max-w-full">
                <button
                    disabled={page === 1}
                    onClick={goPrevious}
                    className="px-3 py-1 rounded border disabled:opacity-40"
                >
                    Previous
                </button>
                {tabletWindow.map((item, index) =>
                    typeof item === "number" ? (
                        <PageButton
                            key={item}
                            page={item}
                            active={item === page}
                            onClick={() => onPageChange(item)}
                        />
                    ) : (
                        <Ellipsis key={`${item}-${index}`} />
                    ),
                )}
                <button
                    disabled={page === totalPages}
                    onClick={goNext}
                    className="px-3 py-1 rounded border disabled:opacity-40"
                >
                    Next
                </button>
            </div>

            {/* Desktop: prev/next + wider window of nearby page numbers */}
            <div className="hidden md:flex flex-wrap items-center justify-center gap-2 max-w-full">
                <button
                    disabled={page === 1}
                    onClick={goPrevious}
                    className="px-3 py-1 rounded border disabled:opacity-40"
                >
                    Previous
                </button>
                {desktopWindow.map((item, index) =>
                    typeof item === "number" ? (
                        <PageButton
                            key={item}
                            page={item}
                            active={item === page}
                            onClick={() => onPageChange(item)}
                        />
                    ) : (
                        <Ellipsis key={`${item}-${index}`} />
                    ),
                )}
                <button
                    disabled={page === totalPages}
                    onClick={goNext}
                    className="px-3 py-1 rounded border disabled:opacity-40"
                >
                    Next
                </button>
            </div>
        </nav>
    );
};

export default Pagination;
